import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import { browser } from '$app/environment';
import { env } from '$env/dynamic/public';
import { isDoNotTrackEnabled } from '$lib/features/consent/logic/consent';

const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';
const POSTHOG_KEY = env.PUBLIC_POSTHOG_KEY ?? import.meta.env.VITE_POSTHOG_KEY ?? '';
const runtimePostHogHost = env.PUBLIC_POSTHOG_HOST?.trim();
const vitePostHogHost = import.meta.env.VITE_POSTHOG_HOST?.trim();
const POSTHOG_HOST = runtimePostHogHost
	? runtimePostHogHost
	: vitePostHogHost
		? vitePostHogHost
		: DEFAULT_POSTHOG_HOST;
const ALLOWED_EVENT_PROPERTIES: Readonly<Record<string, readonly string[]>> = {
	$pageview: [],
	$pageleave: [],
	signup_completed: [],
	tracker_created: ['is_first_tracker'],
	entry_created: ['channel', 'is_first_entry']
};
const STATIC_PAGEVIEW_PATHS = new Set([
	'/',
	'/auth/login',
	'/auth/register',
	'/changelog',
	'/features',
	'/pricing',
	'/privacy',
	'/terms',
	'/wiki',
	'/app',
	'/app/ai',
	'/app/insights',
	'/app/integrations',
	'/app/integrations/strava',
	'/app/journal',
	'/app/onboarding',
	'/app/stats',
	'/app/trackers',
	'/app/trackers/create',
	'/app/trackers/new',
	'/settings',
	'/settings/archive',
	'/settings/billing',
	'/settings/categories',
	'/settings/notifications',
	'/settings/preferences',
	'/settings/trash'
]);
const DYNAMIC_PAGEVIEW_ROUTES: readonly [RegExp, string][] = [
	[/^\/app\/trackers\/[^/]+$/, '/app/trackers/{id}'],
	[/^\/app\/trackers\/[^/]+\/edit$/, '/app/trackers/{id}/edit'],
	[/^\/app\/ai\/[^/]+$/, '/app/ai/{conversation_id}'],
	[/^\/category\/[^/]+$/, '/category/{slug}'],
	[/^\/wiki\/.+$/, '/wiki/{slug}']
];
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
let lastCapturedNavigationPath: string | null = null;
let postHogInitialized = false;

function pageViewRoute(path: string): string | null {
	const withoutLocale = path.replace(/^\/(?:pl|en)(?=\/|$)/, '') || '/';
	if (STATIC_PAGEVIEW_PATHS.has(withoutLocale)) return withoutLocale;
	return DYNAMIC_PAGEVIEW_ROUTES.find(([pattern]) => pattern.test(withoutLocale))?.[1] ?? null;
}

function doNotTrackEnabled(): boolean {
	if (!browser) return false;
	const nav = navigator as Navigator & { msDoNotTrack?: string };
	const win = window as Window & { doNotTrack?: string };
	return isDoNotTrackEnabled(nav.doNotTrack, win.doNotTrack, nav.msDoNotTrack);
}

function pageViewPath(properties: Record<string, unknown>): string | null {
	for (const key of ['path', '$pathname', '$current_url']) {
		const value = properties[key];
		if (typeof value !== 'string') continue;
		try {
			return new URL(value, 'https://samlla.invalid').pathname;
		} catch {
			// Try the next explicitly allowed location field.
		}
	}
	return null;
}

function pageViewLocation(properties: Record<string, unknown>): Record<string, unknown> {
	const rawPath = pageViewPath(properties);
	const pathname = rawPath ? pageViewRoute(rawPath) : null;
	if (!pathname) return {};

	let origin: string | null = browser ? window.location.origin : null;
	let host: string | null = browser ? window.location.host : null;
	const currentUrl = properties.$current_url;
	if ((!origin || !host) && typeof currentUrl === 'string') {
		try {
			const parsed = new URL(currentUrl);
			origin = parsed.origin;
			host = parsed.host;
		} catch {
			// The route template is still safe to retain without an invalid origin.
		}
	}

	const safe: Record<string, unknown> = { $pathname: pathname };
	if (origin && host) {
		safe.$current_url = `${origin}${pathname}`;
		safe.$host = host;
	}
	for (const key of ['$session_id', '$window_id']) {
		const value = properties[key];
		if (typeof value === 'string') safe[key] = value;
	}
	return safe;
}

function safeEventProperties(
	name: string,
	properties?: Record<string, unknown>
): Record<string, unknown> {
	const allowed = ALLOWED_EVENT_PROPERTIES[name];
	if (!allowed || !properties) return {};
	if (name === '$pageview' || name === '$pageleave') return pageViewLocation(properties);
	const safe: Record<string, unknown> = {};
	for (const key of allowed) {
		const value = properties[key];
		if (typeof value === 'boolean') safe[key] = value;
		if (
			key === 'channel' &&
			typeof value === 'string' &&
			['manual', 'quick_add', 'integration', 'api'].includes(value)
		) {
			safe[key] = value;
		}
	}
	return safe;
}

function scrubPostHogEnvelope(envelope: CaptureResult | null): CaptureResult | null {
	if (!envelope) return null;
	if (typeof envelope.event !== 'string' || !(envelope.event in ALLOWED_EVENT_PROPERTIES)) {
		return null;
	}
	const name = envelope.event;
	const isNavigationEvent = name === '$pageview' || name === '$pageleave';
	const properties = safeEventProperties(name, envelope.properties);
	if (isNavigationEvent && typeof properties.$pathname !== 'string') return null;
	properties.token = POSTHOG_KEY;
	const distinctId = envelope.properties?.distinct_id;
	if (typeof distinctId === 'string' && UUID_PATTERN.test(distinctId)) {
		properties.distinct_id = distinctId;
	}
	const uuid = isNavigationEvent && typeof envelope.uuid === 'string' ? envelope.uuid : undefined;
	const timestamp =
		isNavigationEvent &&
		(typeof envelope.timestamp === 'string' || envelope.timestamp instanceof Date)
			? envelope.timestamp
			: undefined;
	const mutableEnvelope = envelope as unknown as Record<string, unknown>;
	for (const key of Object.keys(mutableEnvelope)) delete mutableEnvelope[key];
	Object.assign(envelope, { event: name, properties });
	if (uuid) envelope.uuid = uuid;
	if (timestamp) envelope.timestamp = timestamp;
	return envelope;
}

function initPostHog(): boolean {
	if (!browser || postHogInitialized || doNotTrackEnabled()) return postHogInitialized;
	if (!POSTHOG_KEY) {
		console.info('[posthog] PUBLIC_POSTHOG_KEY/VITE_POSTHOG_KEY is not set, skipping init');
		return false;
	}

	posthog.init(POSTHOG_KEY, {
		api_host: POSTHOG_HOST,
		capture_pageview: false,
		capture_pageleave: false,
		autocapture: false,
		capture_dead_clicks: false,
		disable_session_recording: true,
		disable_surveys: true,
		// Browser flags are resolved exclusively from typed defaults and local
		// overrides. Disable both the legacy feature-flag path and the current
		// flags/remote-config request in posthog-js 1.367.
		advanced_disable_feature_flags: true,
		advanced_disable_flags: true,
		persistence: 'memory',
		person_profiles: 'identified_only',
		// GDPR (SAM-194): never capture until the user explicitly opts in via the
		// consent banner, and honour the browser's Do Not Track signal.
		opt_out_capturing_by_default: true,
		respect_dnt: true,
		before_send: scrubPostHogEnvelope,
		// Also block client-side persistence (distinct_id, identify writes) until
		// opt-in. `capture()` is gated by opt-out, but `identify()` writes to
		// storage regardless — this closes that PII-without-consent leak at the SDK.
		opt_out_persistence_by_default: true
	});
	postHogInitialized = true;
	return true;
}

/**
 * Opt the user into capturing after they accept the consent banner.
 */
export function optInPostHog(): void {
	if (!browser || !POSTHOG_KEY) return;
	if (doNotTrackEnabled()) return;
	if (!initPostHog()) return;
	posthog.opt_in_capturing();
	trackPageView(window.location.href);
}

/**
 * Opt the user out of capturing (consent declined or withdrawn).
 */
export function optOutPostHog(): void {
	if (!browser || !POSTHOG_KEY) return;
	lastCapturedNavigationPath = null;
	if (!postHogInitialized) return;
	posthog.opt_out_capturing();
	posthog.reset();
}

/**
 * Identify an opted-in user by UUID only. Person properties are never accepted.
 * Registering the UUID in memory avoids PostHog's metadata-rich `$identify`
 * event and `/flags` reload. The final envelope scrubber preserves only that
 * validated UUID plus the explicitly allowlisted event properties.
 */
export function identifyUser(userId: string): boolean {
	if (!browser || !POSTHOG_KEY || !postHogInitialized) return false;
	if (!posthog.has_opted_in_capturing()) return false;
	if (!UUID_PATTERN.test(userId)) return false;
	posthog.register({ distinct_id: userId });
	return true;
}

/**
 * Reset PostHog identity on logout.
 */
export function resetPostHog(): void {
	if (!browser || !POSTHOG_KEY || !postHogInitialized) return;
	const preserveConsent = posthog.has_opted_in_capturing() && !doNotTrackEnabled();
	lastCapturedNavigationPath = null;
	posthog.reset();
	if (preserveConsent) posthog.opt_in_capturing();
}

/**
 * Capture a PostHog event. No-op when PostHog is not configured or running on server.
 */
export function trackEvent(name: string, properties?: Record<string, unknown>): void {
	if (!browser || !POSTHOG_KEY || !postHogInitialized) return;
	if (doNotTrackEnabled()) return;
	if (!posthog.has_opted_in_capturing()) return;
	if (!(name in ALLOWED_EVENT_PROPERTIES)) return;
	posthog.capture(name, safeEventProperties(name, properties));
}

export function trackPageView(rawPath: string): void {
	if (!browser || !POSTHOG_KEY || !postHogInitialized) return;
	if (doNotTrackEnabled()) return;
	if (!posthog.has_opted_in_capturing()) return;
	let rawUrl: URL;
	try {
		rawUrl = new URL(rawPath, window.location.origin);
	} catch {
		return;
	}
	const pathname = pageViewRoute(rawUrl.pathname);
	if (!pathname || lastCapturedNavigationPath === rawUrl.pathname) return;
	const captured = posthog.capture('$pageview', {
		$current_url: `${window.location.origin}${pathname}`,
		$pathname: pathname,
		$host: window.location.host
	});
	if (captured) lastCapturedNavigationPath = rawUrl.pathname;
}
